import "semantic-ui-css/semantic.min.css";
import React, { useState, useEffect, useRef } from "react";
import "./GlobalSearch.css";
import { useSite } from "../../context/siteContext";
import {
	Box,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { ASSETS_BASE_URL, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL, SERVER_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import { searchProducts } from "../../services/globalSearchService";
import DocCardImage from "../DocCardImage/DocCardImage";
import { useAuth } from "../../context/authContext";
import useAccessiblePages from "../../hooks/useAccessiblePages";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { ROUTES } from "../../constants";
import { buildExternalLoginUrl } from "../../utilities/externalDocs";
import { formatMachineNumberFromRow } from "../../utilities/machineDisplay";

const parseJsonArray = (value) => {
	if (Array.isArray(value)) return value;
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
};

const parseNumberArray = (value) => {
	if (Array.isArray(value)) {
		return value.map((val) => Number(val)).filter((num) => Number.isFinite(num));
	}
	if (typeof value === "string") {
		const trimmed = value.replace(/^\{|\}$/g, "");
		if (!trimmed) return [];
		return trimmed
			.split(",")
			.map((val) => Number(val.trim()))
			.filter((num) => Number.isFinite(num));
	}
	return [];
};

const getLineName = (line) => (
	line?.line_name || line?.site_line_name || line?.name || line?.sub_name || ""
);

const buildLineMachinesHref = (line) => {
	const params = new URLSearchParams();
	if (line?.product_id !== undefined && line?.product_id !== null) {
		params.set("site_line_id", line.product_id);
	}
	const lineName = getLineName(line);
	if (lineName) params.set("line_name", lineName);
	if (line?.line_number) params.set("line_number", line.line_number);
	const query = params.toString();
	return `${ROUTES.MACHINES}${query ? `?${query}` : ""}`;
};

const buildDocSubtitle = (doc, t) => {
	const machines = parseJsonArray(doc?.machine_details).map(
		(machine) => machine?.machine_number || machine?.machine_name
	).filter(Boolean);
	const categories = parseJsonArray(doc?.categories)
		.map((cat) => cat?.category_name)
		.filter(Boolean);

	const summarize = (values) => {
		const limit = 2;
		if (values.length <= limit) return values.join(", ");
		return `${values.slice(0, limit).join(", ")} +${values.length - limit} more`;
	};

	const machineLabel = t("UI_TEXT.MACHINES") || "Machines";
	const rawCategoryLabel = t("UI_TEXT.CATEGORIES");
	const categoryLabel =
		rawCategoryLabel && rawCategoryLabel !== "UI_TEXT.CATEGORIES"
			? rawCategoryLabel
			: "Categories";

	const parts = [];
	if (machines.length) {
		parts.push(`${machineLabel}: ${summarize(machines)}`);
	}
	if (categories.length) {
		parts.push(`${categoryLabel}: ${summarize(categories)}`);
	}
	return parts.join(" • ");
};

const getDocumentLink = (item) => {
	const type = String(item?.attachment_type || "").toLowerCase();
	if (type === "url" && item?.attachment) {
		return item.attachment;
	}
	if (type === "file" && item?.attachment) {
		const filename = String(item.attachment).split("/").pop() || item.name || "document";
		return `${SERVER_BASE_URL}/assets/Documents/${encodeURIComponent(filename)}`;
	}
	return null;
};

const getExternalDocumentsLoginUrl = ({ enabled, templateUrl, selectedSiteData }) => {
	if (!enabled || !templateUrl) return "";
	const username = selectedSiteData?.amf_method_username_ln;
	const password = selectedSiteData?.amf_method_password_ln;
	return buildExternalLoginUrl({
		templateUrl,
		username,
		password,
	});
};

const resolveAssetImage = (raw, stockImageUrl) => {
	if (raw) {
		if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
		const path = raw.startsWith("/") ? raw : `/${raw}`;
		return `${ASSETS_BASE_URL}${path}`;
	}
	return stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
};

const ResultText = ({ primary, secondary }) => (
	<div className="result-info">
		<Tooltip title={primary || ""} placement="top" arrow>
			<Typography className="result-title">{primary}</Typography>
		</Tooltip>
		{secondary ? (
			<Tooltip title={secondary} placement="top" arrow>
				<div className="category-text">{secondary}</div>
			</Tooltip>
		) : null}
	</div>
);

const matchesPageSearch = (page, term) => {
	const normalizedTerm = String(term || "").trim().toLowerCase();
	if (!normalizedTerm) return true;
	const haystack = [
		page?.name,
		...(Array.isArray(page?.searchTerms) ? page.searchTerms : []),
	]
		.filter(Boolean)
		.map((value) => String(value).toLowerCase());
	return haystack.some((value) => value.includes(normalizedTerm));
};

const getMatchingPages = (pages, term, partsOnly, user) => {
	if (partsOnly) return [];
	if (!String(term || "").trim()) return [];
	
	return pages.filter((item) => {
		// Filter out Admin pages for users with "user" role type
		if (item.name === "Admin" && item.href === "/admin") {
			const userRoleType = user?.role?.role_type || user?.user_role;
			if (userRoleType?.toLowerCase() === "user") {
				return false; // Don't show Admin in search for user role type
			}
		}
		
		return matchesPageSearch(item, term);
	});
};

const GlobalSearch = ({ width = "100%", source, partsOnly = false, ...rest }) => {
	const [machines, setMachines] = useState([]); // State to store Machines
	const [products, setProducts] = useState([]); // State to store Products
	const { selectedSite, selectedSiteData } = useSite();
	const { user } = useAuth();
	const accessiblePages = useAccessiblePages();
	const { stockImageUrl } = useBrandingAssets();
	const [searchTerm, setSearchTerm] = useState("");
	const [data, setData] = useState([]); // State to store Products
	const [filteredData, setFilteredData] = useState(data);
	const navigate = useNavigate();
	const [isFocused, setIsFocused] = useState(false);
	const containerRef = useRef(null);
	const [resultsLoading, setResultsLoading] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		const pageResults = getMatchingPages(accessiblePages, searchTerm, partsOnly, user);

		// Allow static page suggestions even before API search is available.
		if (!searchTerm) {
			setData([]);
			setFilteredData([]);
			setResultsLoading(false);
			return;
		}

		if (searchTerm.length < 2 || !selectedSite) {
			setData(pageResults);
			setFilteredData(pageResults);
			setResultsLoading(false);
			return;
		}

		const search = async () => {
			setResultsLoading(true);
			try {
				const reqBody = {
					site_number: selectedSite,
					searchTerm: searchTerm, // Use the real-time search term
					site_id: selectedSiteData?.site_id,
					cus_id: selectedSiteData?.cus_id,
					...(user?.user_id ? { user_id: user.user_id } : {}),
				};
				console.log("selectedSiteData global: ",selectedSiteData)

				// Call your NEW fast search endpoint
					const response = await searchProducts(reqBody);
				if (response.success) {
					// The API now returns a small, pre-filtered list.
					// We also add the static "Pages" results here.
					const pageResults = getMatchingPages(accessiblePages, searchTerm, partsOnly, user);

					// Add href and image to the dynamic results from the backend
					const apiResults = response.data.map(item => {
						if (item.category === 'Products') {
							const raw = item?.asset_data?.images?.[0];
							const imageKey = item.sub_name || item.name;
							return {
								...item,
								href: `/machines/${item.machine_id}/${item.product_id}`,
								image: raw
									? resolveAssetImage(raw, stockImageUrl)
									: (imageKey
										? `${ASSETS_BASE_URL}/ProductImages/${imageKey}.png`
										: stockImageUrl || `${ASSETS_BASE_URL}/stock.png`),
							};
						}
						if (item.category === 'Machines') {
							const raw = item?.asset_data?.images?.[0];
							return {
								...item,
								href: `/machines/${item.machine_id}`,
								image: raw
									? resolveAssetImage(raw, stockImageUrl)
									: `${ASSETS_BASE_URL}/MachineImages/${item.sub_name}.png`
							};
						}
						if (item.category === 'Documents') {
							return {
								...item,
								categories: parseJsonArray(item.categories),
								machine_details: parseJsonArray(item.machine_details),
								assigned_machines: parseNumberArray(item.assigned_machines),
							};
						}
						if (item.category === 'Lines') {
							return {
								...item,
								href: buildLineMachinesHref(item),
							};
						}
						return item;
					});

					const combinedResults = [...pageResults, ...apiResults];
					const filteredResults = partsOnly
						? combinedResults.filter(
							(item) => item.category === "Products" || item.category === "Machines"
						)
						: combinedResults;
					setData(filteredResults);
					setFilteredData(filteredResults);
				}
			} catch (error) {
				console.error("Error during search", error);
			} finally {
				setResultsLoading(false);
			}
		};

		// Use a timeout to debounce the search, so we don't spam the API on every keystroke
		const debounceTimeout = setTimeout(() => {
			search();
		}, 300); // 300ms delay

		// Cleanup function to cancel the timeout if the user keeps typing
		return () => clearTimeout(debounceTimeout);

	}, [searchTerm, selectedSite, selectedSiteData, accessiblePages, user?.user_id, partsOnly, user]); // This effect now runs only when the search term or site changes

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchTerm(value);
		setResultsLoading(true);

		// Filter the data based on the search term
	};

	useEffect(() => {
		if (searchTerm === "") {
			setFilteredData(data);
		} else {
			if (searchTerm.length >= 2) {
				const filtered = data.filter(
					(item) =>
						(item.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
						(item.category || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
						(item.sub_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
						(item.category === "Pages" && matchesPageSearch(item, searchTerm))
				);
				setFilteredData(filtered);
			}
		}
	}, [searchTerm, data]);

	const handleItemClick = (item) => {
		setIsFocused(false);
		if (item.category === "Documents") {
			const externalLoginUrl = getExternalDocumentsLoginUrl({
				enabled: DOCUMENT_EXTERNAL_ENABLED,
				templateUrl: DOCUMENT_EXTERNAL_URL,
				selectedSiteData,
			});
			if (externalLoginUrl) {
				window.open(externalLoginUrl, "_blank", "noopener,noreferrer");
				return;
			}
			const href = getDocumentLink(item);
			if (href) {
				window.open(href, "_blank", "noopener,noreferrer");
			} else {
				navigate("/documents");
			}
			return;
		}
		if (item.category === "Orders") {
			const params = new URLSearchParams();
			const orderId = item?.order_id ?? item?.product_id;
			const orderNumber = item?.order_number ?? item?.name;
			if (orderId) params.set("open_id", orderId);
			if (orderNumber) params.set("open_number", orderNumber);
			navigate(`/order-history${params.toString() ? `?${params.toString()}` : ""}`);
			return;
		}
		if (item.category === 'Pages' && item.name === "Documents") {
			const loginUrl = getExternalDocumentsLoginUrl({
				enabled: DOCUMENT_EXTERNAL_ENABLED,
				templateUrl: DOCUMENT_EXTERNAL_URL,
				selectedSiteData,
			});
			if (!loginUrl) {
				navigate(ROUTES.DOCUMENT);
				return;
			}
			window.open(loginUrl, "_blank", "noopener,noreferrer");
			return;
		}
		navigate(`${item.href}`);
		// You can perform any action on click here, like navigating or showing details
	};

	const handleDocumentCategoryClick = (categoryName) => {
		if (!categoryName) return;
		setIsFocused(false);
		navigate(`/documents?category=${encodeURIComponent(categoryName)}`);
	};

	const triggerFullSearch = () => {
		if (!searchTerm || searchTerm.length < 1) return;
		setIsFocused(false);
		const params = new URLSearchParams();
		params.set("q", searchTerm);
		if (partsOnly) params.set("partsonly", "true");
		navigate(`/search-results?${params.toString()}`);
	};

	const handleImgError = (e) => {
		const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
		e.target.onerror = null; // Prevent infinite loop
		e.target.src = defaultImage; // Fallback to default image
	};

	// Group filtered data by category
	const categorizedData = {
		Pages: filteredData.filter((item) => item.category === "Pages"),
		Products: filteredData.filter((item) => item.category === "Products"),
		Machines: filteredData.filter((item) => item.category === "Machines"),
		Documents: filteredData.filter((item) => item.category === "Documents"),
		Orders: filteredData.filter((item) => item.category === "Orders"),
		Lines: filteredData.filter((item) => item.category === "Lines"),
	};

	const documentCategories = React.useMemo(() => {
		const docs = categorizedData.Documents;
		if (!docs.length) return [];
		const map = new Map();
		docs.forEach((doc) => {
			const categories = Array.isArray(doc.categories) ? doc.categories : [];
			categories.forEach((cat) => {
				const name = (cat?.category_name || "").trim();
				if (!name) return;
				const entry = map.get(name) || { name, count: 0 };
				entry.count += 1;
				map.set(name, entry);
			});
		});
		return Array.from(map.values()).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
	}, [categorizedData.Documents]);

	useEffect(() => {
		if (filteredData.length >= 0) {
			setResultsLoading(false);
		}
	}, [filteredData]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsFocused(false);
				setSearchTerm("");
			}
		}

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div className="search-container" style={{ width }} ref={containerRef} data-tour="main-page-search" {...rest}>
			<TextField
				variant="outlined"
				placeholder={t('PLACEHOLDER.SEARCHS')}
				fullWidth
				value={searchTerm}
				onChange={handleSearchChange}
				onFocus={() => setIsFocused(true)}
				size="small"
				slotProps={{
					input: {
						endAdornment: (
							<Box display="flex">
								<IconButton marginRight="-2px" onClick={triggerFullSearch}>
									<SearchIcon />
								</IconButton>
							</Box>
						),
					},
				}}
				sx={{
					"& .MuiInputBase-root": {
						paddingRight: "2px", // Ensure there's no extra padding in the input field itself
					},
					"& .MuiOutlinedInput-root": {
						borderRadius: "40px",
						backgroundColor: "#fff",
					},
					"& .MuiInputBase-input::placeholder": {
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					},
					width: "100%",
				}}
				onKeyDown={(e) => { if (e.key === 'Enter') { triggerFullSearch(); } }}
			/>

			{/* The dropdown-style container for search results */}
			{source !== 'page' && isFocused && searchTerm && (
				<Paper className="search-results">
					{resultsLoading ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								padding: "10px",
								flexDirection: "row",
								columnGap: "10px",
							}}>
							<CircularProgress color="inherit" size="25px" />
							<Typography variant="body1" fontSize="11px">
								{t('UI_TEXT.LOADING')}
							</Typography>
						</Box>
					) : (
						<List>
							{/* Display Pages Results */}
							{categorizedData.Pages.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										{t('UI_TEXT.PAGES')}
									</Typography>
									{categorizedData.Pages.map((item, index) => (
										<ListItem
											key={index}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<img
												src={item.image}
												alt={item.name}
												onError={handleImgError}
												className="result-image"
											/>
											<ResultText primary={item.name} secondary={item.sub_name} />
										</ListItem>
									))}
								</>
							)}

							{/* Display Products Results */}
							{categorizedData.Products.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										{t('UI_TEXT.PARTS')}
									</Typography>
									{categorizedData.Products.map((item, index) => (
										<ListItem
											key={index}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<img
												src={item.image}
												alt={item.name}
												className="result-image"
												onError={handleImgError}
											/>
											<ResultText
												primary={item.name}
													secondary={`${t('UI_TEXT.PART')} ${item.sub_name}${item.machine_number ? ` | ${t('UI_TEXT.MACHINE')} ${formatMachineNumberFromRow(item)}` : ''}`}
												/>
										</ListItem>
									))}
								</>
							)}

							{/* Display Machines Results */}
							{categorizedData.Machines.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										{t('UI_TEXT.MACHINES')}
									</Typography>
									{categorizedData.Machines.map((item, index) => (
										<ListItem
											key={index}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<img
												src={item.image}
												alt={item.name}
												className="result-image"
												onError={handleImgError}
											/>
											<ResultText primary={item.name} secondary={`${t('UI_TEXT.MACHINE')} ${item.sub_name}`} />
										</ListItem>
									))}
								</>
							)}

								{categorizedData.Orders.length > 0 && (
									<>
										<Typography variant="h6" className="category-header">
											{t('LABELS.ORDER')}
										</Typography>
										{categorizedData.Orders.map((item, index) => (
											<ListItem
												key={index}
												className="result-item"
												onClick={() => handleItemClick(item)}>
												<img
													src={stockImageUrl || `${ASSETS_BASE_URL}/stock.png`}
													alt={item.name}
													className="result-image"
													onError={handleImgError}
												/>
												<ResultText primary={item.name} secondary={`${t('UI_TEXT.MACHINE')} ${item.sub_name}`} />
											</ListItem>
										))}
									</>
								)}

							{categorizedData.Documents.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										{t('UI_TEXT.DOCUMENTS')}
									</Typography>
									{categorizedData.Documents.map((item, index) => (
										<ListItem
											key={`doc-${item.product_id}-${index}`}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<DocCardImage
												type={item.attachment_type}
												path={item.attachment}
											/>
											<ResultText primary={item.name} secondary={buildDocSubtitle(item, t)} />
										</ListItem>
									))}
								</>
							)}

							{documentCategories.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										{t('UI_TEXT.DOCUMENT_CATEGORIES')}
									</Typography>
									{documentCategories.map((item) => (
										<ListItem
											key={`doc-cat-${item.name}`}
											className="result-item"
											onClick={() => handleDocumentCategoryClick(item.name)}>
											<ResultText primary={item.name} secondary={`${item.count} ${t('UI_TEXT.DOCUMENTS')}`} />
										</ListItem>
									))}
								</>
							)}

							{categorizedData.Lines.length > 0 && (
								<>
									<Typography variant="h6" className="category-header">
										Lines
									</Typography>
									{categorizedData.Lines.map((item, index) => (
										<ListItem
											key={`line-${item.product_id}-${index}`}
											className="result-item"
											onClick={() => handleItemClick(item)}>
											<ResultText primary={item.name} secondary={item.sub_name || ""} />
										</ListItem>
									))}
								</>
							)}

							{/* Display message when no results are found */}
							{filteredData.length === 0 && (
								<ListItem className="no-results">
									<Typography>No results found</Typography>
								</ListItem>
							)}
						</List>
					)}
				</Paper>
			)}
		</div>
	);
};

export default GlobalSearch;
